import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Package,
  Printer,
  FileText,
  Check,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Store,
  MapPin,
  Phone,
  Mail,
  Hash,
  Sparkles
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const STEPS = [
  { id: 1, key: 'business', icon: Building2 },
  { id: 2, key: 'products', icon: Package },
  { id: 3, key: 'printer', icon: Printer },
  { id: 4, key: 'invoice', icon: FileText },
];

const SetupWizard = () => {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [shopCreated, setShopCreated] = useState(false);
  const [shopId, setShopId] = useState(null);
  
  // Step 1: Business Details
  const [businessData, setBusinessData] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: user?.email || '',
    nif: '',
    businessName: '',
  });
  
  // Step 2: Products
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [productsImported, setProductsImported] = useState(false);
  
  // Step 3: Printer
  const [printerConfig, setPrinterConfig] = useState({
    type: 'none', // none, bluetooth, usb, wifi
    configured: false,
  });
  
  // Step 4: Test Invoice
  const [testInvoiceCreated, setTestInvoiceCreated] = useState(false);

  const translations = {
    es: {
      title: 'Configuración Rápida',
      subtitle: 'Configura tu tienda en 4 sencillos pasos',
      step1: 'Datos del Negocio',
      step2: 'Importar Productos',
      step3: 'Conectar Impresora',
      step4: 'Primera Factura',
      next: 'Siguiente',
      back: 'Atrás',
      skip: 'Saltar',
      finish: 'Finalizar',
      
      // Step 1
      shopName: 'Nombre de la Tienda',
      address: 'Dirección',
      city: 'Ciudad',
      postalCode: 'Código Postal',
      phone: 'Teléfono',
      email: 'Email',
      nif: 'NIF/CIF',
      businessName: 'Razón Social',
      nifHelp: 'Necesario para facturas VeriFactu',
      
      // Step 2
      selectCategories: 'Selecciona las categorías de productos que vendes',
      importProducts: 'Importar Productos',
      productsImportedSuccess: '¡Productos importados correctamente!',
      groceries: 'Alimentación',
      drinks: 'Bebidas',
      tobacco: 'Tabaco',
      press: 'Prensa',
      snacks: 'Aperitivos',
      frozen: 'Congelados',
      fresh: 'Frescos',
      household: 'Hogar',
      health: 'Salud y Belleza',
      
      // Step 3
      printerType: 'Tipo de Impresora',
      noPrinter: 'Configurar más tarde',
      bluetooth: 'Bluetooth',
      usb: 'USB',
      wifi: 'WiFi/Red',
      printerSkip: 'Puedes configurar la impresora después en Ajustes',
      
      // Step 4
      createTestInvoice: 'Crear Factura de Prueba',
      testInvoiceSuccess: '¡Factura de prueba creada!',
      testInvoiceDesc: 'Vamos a crear una factura de prueba para verificar que todo funciona correctamente.',
      
      // Complete
      setupComplete: '¡Configuración Completada!',
      setupCompleteDesc: 'Tu tienda está lista para empezar a vender',
      goToDashboard: 'Ir al Panel',
      goToPOS: 'Abrir TPV',
    },
    en: {
      title: 'Quick Setup',
      subtitle: 'Set up your shop in 4 easy steps',
      step1: 'Business Details',
      step2: 'Import Products',
      step3: 'Connect Printer',
      step4: 'First Invoice',
      next: 'Next',
      back: 'Back',
      skip: 'Skip',
      finish: 'Finish',
      
      shopName: 'Shop Name',
      address: 'Address',
      city: 'City',
      postalCode: 'Postal Code',
      phone: 'Phone',
      email: 'Email',
      nif: 'Tax ID (NIF/CIF)',
      businessName: 'Legal Business Name',
      nifHelp: 'Required for VeriFactu invoices',
      
      selectCategories: 'Select the product categories you sell',
      importProducts: 'Import Products',
      productsImportedSuccess: 'Products imported successfully!',
      groceries: 'Groceries',
      drinks: 'Drinks',
      tobacco: 'Tobacco',
      press: 'Press',
      snacks: 'Snacks',
      frozen: 'Frozen',
      fresh: 'Fresh',
      household: 'Household',
      health: 'Health & Beauty',
      
      printerType: 'Printer Type',
      noPrinter: 'Set up later',
      bluetooth: 'Bluetooth',
      usb: 'USB',
      wifi: 'WiFi/Network',
      printerSkip: 'You can configure the printer later in Settings',
      
      createTestInvoice: 'Create Test Invoice',
      testInvoiceSuccess: 'Test invoice created!',
      testInvoiceDesc: "Let's create a test invoice to verify everything works correctly.",
      
      setupComplete: 'Setup Complete!',
      setupCompleteDesc: 'Your shop is ready to start selling',
      goToDashboard: 'Go to Dashboard',
      goToPOS: 'Open POS',
    }
  };
  
  const t = (key) => translations[language]?.[key] || translations['en'][key] || key;

  const categories = [
    { id: 'groceries', icon: '🛒' },
    { id: 'drinks', icon: '🥤' },
    { id: 'tobacco', icon: '🚬' },
    { id: 'press', icon: '📰' },
    { id: 'snacks', icon: '🍿' },
    { id: 'frozen', icon: '🧊' },
    { id: 'fresh', icon: '🥬' },
    { id: 'household', icon: '🧹' },
    { id: 'health', icon: '💊' },
  ];

  const handleCreateShop = async () => {
    setLoading(true);
    try {
      const response = await api.post('/shops', {
        name: businessData.name,
        address: businessData.address,
        city: businessData.city,
        postal_code: businessData.postalCode,
        phone: businessData.phone,
        email: businessData.email,
        nif: businessData.nif,
        business_name: businessData.businessName || businessData.name,
      });
      setShopId(response.data.id);
      setShopCreated(true);
      setCurrentStep(2);
    } catch (error) {
      console.error('Failed to create shop:', error);
      alert(error.response?.data?.detail || 'Failed to create shop');
    } finally {
      setLoading(false);
    }
  };

  const handleImportProducts = async () => {
    if (selectedCategories.length === 0) {
      setCurrentStep(3);
      return;
    }
    
    setLoading(true);
    try {
      // Import products from catalog for selected categories
      await api.post(`/shops/${shopId}/products/import-catalog`, {
        categories: selectedCategories
      });
      setProductsImported(true);
      setTimeout(() => setCurrentStep(3), 1000);
    } catch (error) {
      console.error('Failed to import products:', error);
      // Continue anyway
      setCurrentStep(3);
    } finally {
      setLoading(false);
    }
  };

  const handlePrinterSetup = () => {
    // For now, just move to next step
    // Actual printer setup would involve WebBluetooth/USB APIs
    setCurrentStep(4);
  };

  const handleCreateTestInvoice = async () => {
    setLoading(true);
    try {
      // Create a test invoice
      await api.post(`/verifactu/invoices/${shopId}`, {
        client_name: 'Cliente de Prueba',
        client_nif: '00000000T',
        items: [
          { description: 'Producto de prueba', quantity: 1, unit_price: 10.00, tax_rate: 21 }
        ],
        is_test: true
      });
      setTestInvoiceCreated(true);
    } catch (error) {
      console.error('Failed to create test invoice:', error);
      // Continue anyway
      setTestInvoiceCreated(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/shop/dashboard');
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('shopName')} *
          </label>
          <div className="relative">
            <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={businessData.name}
              onChange={(e) => setBusinessData({...businessData, name: e.target.value})}
              required
              className="vf-input w-full pl-12 pr-4 py-3"
              placeholder={language === 'es' ? 'Mi Tienda' : 'My Shop'}
              data-testid="setup-shop-name"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('address')} *
          </label>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={businessData.address}
              onChange={(e) => setBusinessData({...businessData, address: e.target.value})}
              required
              className="vf-input w-full pl-12 pr-4 py-3"
              placeholder={language === 'es' ? 'Calle Mayor 1' : '1 Main Street'}
              data-testid="setup-address"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('city')} *
          </label>
          <input
            type="text"
            value={businessData.city}
            onChange={(e) => setBusinessData({...businessData, city: e.target.value})}
            required
            className="vf-input w-full px-4 py-3"
            placeholder="Madrid"
            data-testid="setup-city"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('postalCode')}
          </label>
          <input
            type="text"
            value={businessData.postalCode}
            onChange={(e) => setBusinessData({...businessData, postalCode: e.target.value})}
            className="vf-input w-full px-4 py-3"
            placeholder="28001"
            data-testid="setup-postal"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('phone')} *
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="tel"
              value={businessData.phone}
              onChange={(e) => setBusinessData({...businessData, phone: e.target.value})}
              required
              className="vf-input w-full pl-12 pr-4 py-3"
              placeholder="+34 600 000 000"
              data-testid="setup-phone"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('email')} *
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="email"
              value={businessData.email}
              onChange={(e) => setBusinessData({...businessData, email: e.target.value})}
              required
              className="vf-input w-full pl-12 pr-4 py-3"
              placeholder="tienda@email.com"
              data-testid="setup-email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('nif')} *
          </label>
          <div className="relative">
            <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={businessData.nif}
              onChange={(e) => setBusinessData({...businessData, nif: e.target.value.toUpperCase()})}
              required
              className="vf-input w-full pl-12 pr-4 py-3"
              placeholder="B12345678"
              data-testid="setup-nif"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">{t('nifHelp')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
            {t('businessName')}
          </label>
          <input
            type="text"
            value={businessData.businessName}
            onChange={(e) => setBusinessData({...businessData, businessName: e.target.value})}
            className="vf-input w-full px-4 py-3"
            placeholder={language === 'es' ? 'Comercio S.L.' : 'Business Ltd.'}
            data-testid="setup-business-name"
          />
        </div>
      </div>

      <button
        onClick={handleCreateShop}
        disabled={loading || !businessData.name || !businessData.address || !businessData.city || !businessData.phone || !businessData.email || !businessData.nif}
        className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="setup-step1-next"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            {t('next')}
            <ChevronRight className="w-5 h-5" />
          </>
        )}
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <p className="text-slate-600">{t('selectCategories')}</p>
      
      <div className="grid grid-cols-3 gap-3">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => {
              setSelectedCategories(prev =>
                prev.includes(cat.id)
                  ? prev.filter(c => c !== cat.id)
                  : [...prev, cat.id]
              );
            }}
            className={`p-4 border-2 rounded-sm text-center transition-colors ${
              selectedCategories.includes(cat.id)
                ? 'border-[#22c55e] bg-green-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`setup-category-${cat.id}`}
          >
            <span className="text-2xl">{cat.icon}</span>
            <p className="mt-2 text-sm font-medium text-[#1e3a5f]">
              {t(cat.id)}
            </p>
          </button>
        ))}
      </div>

      {productsImported && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-green-50 border border-green-200 rounded-sm flex items-center gap-3"
        >
          <Check className="w-5 h-5 text-[#22c55e]" />
          <span className="text-green-700">{t('productsImportedSuccess')}</span>
        </motion.div>
      )}

      <div className="flex gap-4">
        <button
          onClick={() => setCurrentStep(1)}
          className="vf-btn-secondary flex-1 flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('back')}
        </button>
        <button
          onClick={handleImportProducts}
          disabled={loading}
          className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="setup-step2-next"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              {selectedCategories.length > 0 ? t('importProducts') : t('skip')}
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <p className="text-slate-600">{t('printerType')}</p>
      
      <div className="grid grid-cols-2 gap-4">
        {[
          { id: 'none', label: t('noPrinter'), icon: '⏭️' },
          { id: 'bluetooth', label: t('bluetooth'), icon: '📶' },
          { id: 'usb', label: t('usb'), icon: '🔌' },
          { id: 'wifi', label: t('wifi'), icon: '🌐' },
        ].map((option) => (
          <button
            key={option.id}
            onClick={() => setPrinterConfig({ type: option.id, configured: option.id !== 'none' })}
            className={`p-4 border-2 rounded-sm text-center transition-colors ${
              printerConfig.type === option.id
                ? 'border-[#22c55e] bg-green-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`setup-printer-${option.id}`}
          >
            <span className="text-2xl">{option.icon}</span>
            <p className="mt-2 text-sm font-medium text-[#1e3a5f]">
              {option.label}
            </p>
          </button>
        ))}
      </div>

      <p className="text-sm text-slate-500 text-center">{t('printerSkip')}</p>

      <div className="flex gap-4">
        <button
          onClick={() => setCurrentStep(2)}
          className="vf-btn-secondary flex-1 flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('back')}
        </button>
        <button
          onClick={handlePrinterSetup}
          className="vf-btn-primary flex-1 flex items-center justify-center gap-2"
          data-testid="setup-step3-next"
        >
          {t('next')}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      {!testInvoiceCreated ? (
        <>
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
              <FileText className="w-10 h-10 text-[#1e3a5f]" />
            </div>
            <p className="text-slate-600">{t('testInvoiceDesc')}</p>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setCurrentStep(3)}
              className="vf-btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              {t('back')}
            </button>
            <button
              onClick={handleCreateTestInvoice}
              disabled={loading}
              className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="setup-create-invoice"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {t('createTestInvoice')}
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-8"
        >
          <div className="w-24 h-24 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <Sparkles className="w-12 h-12 text-[#22c55e]" />
          </div>
          <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2">
            {t('setupComplete')}
          </h3>
          <p className="text-slate-600 mb-8">{t('setupCompleteDesc')}</p>

          <div className="flex gap-4 max-w-md mx-auto">
            <button
              onClick={handleFinish}
              className="vf-btn-secondary flex-1"
              data-testid="setup-go-dashboard"
            >
              {t('goToDashboard')}
            </button>
            <button
              onClick={() => navigate('/shop/pos')}
              className="vf-btn-primary flex-1"
              data-testid="setup-go-pos"
            >
              {t('goToPOS')}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="setup-wizard">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-white border-2 border-[#1e3a5f] p-8"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center">
              <span className="text-white font-bold text-xl">V</span>
            </div>
            <span className="text-xl font-bold text-[#1e3a5f]">VendFactura</span>
          </div>
          <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('title')}</h2>
          <p className="text-slate-600">{t('subtitle')}</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    currentStep > step.id
                      ? 'bg-[#22c55e] text-white'
                      : currentStep === step.id
                      ? 'bg-[#1e3a5f] text-white'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <span className={`text-xs mt-2 ${
                  currentStep >= step.id ? 'text-[#1e3a5f] font-medium' : 'text-slate-400'
                }`}>
                  {t(`step${step.id}`)}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    currentStep > step.id ? 'bg-[#22c55e]' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SetupWizard;
